import { styled } from '@linaria/react'
import React, { type ComponentProps, type FC, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { VoteButtonGroup } from '../components/VoteButtonGroup'
import type { BoxChoiceDto } from '../types/generated'
import { Rhytm } from './Rhytm'
import { TitleAndSubtitle } from './TitleAndSubtitle'
import PeopleIcon from '../svgr/People'
import CoinIcon from '../svgr/Coin'
import TonSymbolIcon from '../svgr/TonSymbol'
import { Typography } from './Typography'
import { colors } from '../styles/colors'
import { BoxImage } from '../components/BoxImage'
import ArrowRight from '../svgr/ArrowRight'
import type { BoxStatusBar, TagType } from './BoxStatusBar'
import { faqPagePath } from '../pages/FaqPage'

const StatsComponent = ({
  totalParticipants,
  totalTonStaked,
}: {
  totalParticipants?: number
  totalTonStaked?: number
}) => {
  return (
    <StatsWrapper>
      <StatItem>
        <PeopleIcon />
        <Typography fontSize={10} font="roboto">
          {totalParticipants || 0}
        </Typography>
      </StatItem>

      <StatItem>
        <CoinIcon />
        <Typography fontSize={10} font="roboto">
          {totalTonStaked || 0}
        </Typography>
        <TonSymbolStyled />
      </StatItem>
    </StatsWrapper>
  )
}

export const BoxView: FC<{
  boxChoices: BoxChoiceDto[]
  boxId: number
  description?: string
  expiration: ReactNode
  imgSrc?: string
  isExpired: boolean
  isSingleBox?: boolean
  isUnderModeration?: boolean
  onBoxChoiceClick: (index: number) => void
  onRewardClaim?: () => void
  progressPercentage?: number
  title: ReactNode
  totalParticipants?: number
  totalTonStaked?: number
  tags: Record<TagType, boolean>
  winnerChoiceIndex: number | null
}> = ({
  boxChoices,
  boxId,
  description,
  expiration,
  imgSrc,
  isExpired,
  isSingleBox,
  isUnderModeration,
  onBoxChoiceClick,
  onRewardClaim,
  progressPercentage,
  title,
  totalParticipants,
  totalTonStaked,
  tags,
  winnerChoiceIndex,
}) => {
  const navigate = useNavigate()
  const hasUserVote = boxChoices.some(choice =>
    Boolean(choice.user_vote_summary),
  )

  const getStatus = (): ComponentProps<typeof BoxStatusBar>['status'] => {
    return undefined
  }

  return (
    <BoxContainer data-testid="box-card" data-box-id={boxId}>
      <BoxImage
        boxId={boxId}
        expiration={expiration}
        hasYourVote={hasUserVote}
        imgSrc={imgSrc}
        isExpired={isExpired}
        isUnderModeration={isUnderModeration}
        onRewardClaim={onRewardClaim}
        progressPercentage={progressPercentage}
        status={getStatus()}
        tags={tags}
      />

      <BoxContent isSingleBox={isSingleBox}>
        <MainInfoContainer isSingleBox={isSingleBox}>
          <TitleSection isSingleBox={isSingleBox}>
            <TitleAndSubtitle
              title={title}
              subtitle={isSingleBox ? description : undefined}
              titleFontSize={isSingleBox ? 20 : 16}
              isShowSeparator={false}
            />
          </TitleSection>

          <StatsSection isSingleBox={isSingleBox}>
            <StatsComponent
              totalParticipants={totalParticipants}
              totalTonStaked={totalTonStaked}
            />

            {isSingleBox && (
              <HowItWorksButton
                onClick={async () =>
                  await navigate(`${faqPagePath}?section=whatIsTheBox`)
                }
              >
                <Typography fontSize={12} fontWeight={600}>
                  <FormattedMessage
                    id="box.howItWorks"
                    defaultMessage="Как это работает?"
                  />
                </Typography>
                <ArrowRight />
              </HowItWorksButton>
            )}
          </StatsSection>
        </MainInfoContainer>

        <VoteButtonGroup
          boxChoices={boxChoices}
          onSelect={onBoxChoiceClick}
          isOpen={!isExpired}
          isProfitabilityShow={true}
          winnerChoiceIndex={winnerChoiceIndex}
        />
      </BoxContent>
    </BoxContainer>
  )
}

const StatsSection = styled.div<{ isSingleBox?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
`

const StatsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    color: ${colors.iconInactive};
  }
`

const TonSymbolStyled = styled(TonSymbolIcon)`
  width: 8px;
  height: 8px;
  margin-left: 0;
`
const BoxContent = styled(Rhytm)<{ isSingleBox?: boolean }>`
  padding: 16px;
  gap: 12px;
`

const BoxContainer = styled.div`
  background: ${colors.bgCard};
  border-radius: ${colors.radiusCardLg};
  overflow: hidden;
  border: 1px solid ${colors.borderCard};
  box-shadow: ${colors.shadowSm};
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
`

const HowItWorksButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.textSecondary};

  svg {
    width: 12px;
    height: 12px;
  }
`
const MainInfoContainer = styled.div<{ isSingleBox?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TitleSection = styled.div<{ isSingleBox?: boolean }>`
  order: ${({ isSingleBox }) => (isSingleBox ? 1 : 2)};
`
